var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { useCallback, useEffect, useState } from 'react';
import { ReactEditor } from 'slate-react';
import { bulletinboardsApi } from 'apis/bulletinboards';
import { appActions } from 'app/app.actions';
import { useNotifications } from 'hooks/useNotifications';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { deserializeFromHtml } from '../components/richText/utils/deserializeFromHtml';
import { EmptyDocument } from '../components/richText/utils/editorUtils';
import { serializeToHtml } from '../components/richText/utils/serializeToHtml';
import { useLoadBulletin } from './useLoadBulletin';
/**
 * Keeps track of the initial and edited states of the bulletin.
 *
 * @param param0 Data required to load the correct bulletin
 */
export function useBulletinEditor({ messengerId, bulletinId, editor, }) {
    const dispatch = useAppDispatch();
    const navigate = useAppNavigate();
    const notify = useNotifications();
    const [viewMode, setViewModeInternal] = useState('editor');
    const [title, setTitle] = useState('');
    const [content, setContent] = useState(EmptyDocument);
    const [initialContent, setinitialContent] = useState(EmptyDocument);
    const [updateBulletin, { isLoading: isUpdating }] = bulletinboardsApi.useUpdateBulletinMutation();
    const notifyError = useCallback((message) => notify({ title: message, type: 'error' }), [notify]);
    const notifySuccess = useCallback((message) => notify({ title: message, type: 'success' }), [notify]);
    const setViewMode = (mode) => {
        // Value in slate is now only initialState, so change the editors document
        // to either the current work in progress or the unedited document 'initialState'
        editor.children = mode === 'editor' ? content : initialContent;
        setViewModeInternal(mode);
    };
    const { isFetching, bulletinBoardId, bulletin, hasError: hasLoadError, } = useLoadBulletin({
        messengerId,
        bulletinId,
    });
    useEffect(() => {
        if (hasLoadError) {
            notifyError('Sorry, there has been an problem loading the bulletin, please try again.');
        }
    }, [notifyError, hasLoadError]);
    const isActive = (bulletin === null || bulletin === void 0 ? void 0 : bulletin.active) || false;
    const resetChanges = () => {
        // Deselect the editor so that the toolbar buttons don't try to update "active" state
        // while document is updating, which causes a crash.
        ReactEditor.deselect(editor);
        editor.children = initialContent;
        setContent(initialContent);
        setViewModeInternal('editor');
    };
    useEffect(() => {
        if (!bulletin)
            return;
        const serializedContent = deserializeFromHtml(bulletin.content);
        setContent(serializedContent);
        editor.children = serializedContent;
        setinitialContent(serializedContent);
        setTitle(bulletin.title);
    }, [bulletin, editor]);
    const hasChanges = !isFetching && (content !== initialContent || title !== (bulletin === null || bulletin === void 0 ? void 0 : bulletin.title));
    const saveChanges = () => __awaiter(this, void 0, void 0, function* () {
        if (!bulletinBoardId)
            return;
        // Deselect the editor so that the toolbar buttons don't try to update "active" state
        // while document is updating, which causes a crash.
        ReactEditor.deselect(editor);
        const convertedContent = serializeToHtml(content);
        try {
            yield updateBulletin({
                boardId: bulletinBoardId,
                bulletinId,
                update: {
                    content: convertedContent,
                    title,
                },
            });
            const noteTitle = isActive
                ? 'Your content has been published. Nice!'
                : 'Your content has been saved.';
            notifySuccess(noteTitle);
        }
        catch (error) {
            notifyError('Sorry, there has been an error saving your changes. Please try again.');
        }
    });
    const goBackToMessengerSettings = () => __awaiter(this, void 0, void 0, function* () {
        if (hasChanges) {
            const { isConfirmed } = yield dispatch(appActions.showModal({
                modal: {
                    type: 'app:confirm',
                    arg: {
                        title: 'Unsaved changes',
                        description: 'Are you sure you want to leave? You have unsaved changes, if you leave you will loose your changes.',
                        confirm: {
                            label: 'Yes, leave',
                        },
                    },
                },
            }));
            if (!isConfirmed)
                return;
        }
        navigate({
            path: '/settings/dixa-messenger/appearance/:messengerId',
            params: { path: { messengerId } },
        });
    });
    return {
        content,
        discardChanges: resetChanges,
        goBackToMessengerSettings,
        hasChanges,
        hasLoadError,
        initialContent,
        initialTitle: (bulletin === null || bulletin === void 0 ? void 0 : bulletin.title) || '',
        isActive,
        isFetching,
        isUpdating,
        saveChanges,
        setContent,
        setTitle,
        setViewMode,
        title,
        viewMode,
    };
}
